import { useCallback, useEffect, useRef, useState } from "react";

import { runCopilotToolProposalAsync } from "../../app/lazyModules";
import type { CopilotBrowserToolResult } from "../../copilotApply";
import type { CarringtonEngineFixture } from "../../impactPreview";
import type {
  CopilotToolProposal,
  CopilotTurnRequest,
  CopilotTurnResponse,
  LiveHealthResponse,
  PostCopilotTurnOptions
} from "../../liveLanes";
import type {
  AssistantGroundingState,
  AssistantMessage,
  AssistantTechnicalDetails,
  AssistantTurnEvent,
  AssistantTurnState
} from "../assistant/types";

export type LiveLaneAvailability =
  | { state: "checking" }
  | { state: "ready"; health: LiveHealthResponse }
  | { state: "disabled"; health: LiveHealthResponse; message: string }
  | { state: "unavailable"; message: string; health?: LiveHealthResponse };

export function copilotAvailabilityFromHealth(
  health: LiveHealthResponse
): LiveLaneAvailability {
  const capability = copilotHealthCapabilityState(health);

  if (capability === "disabled") {
    return {
      state: "disabled",
      health,
      message:
        health.livePath.disabledReason ??
        "Live co-pilot answers are disabled by the local server."
    };
  }

  if (capability === "unavailable") {
    return {
      state: "unavailable",
      health,
      message: liveUnavailableMessage(health)
    };
  }

  // Explicit offline/mock mode remains usable for local review, as before. It
  // is not a fallback from a failed live provider request.
  return { state: "ready", health };
}

export interface CopilotProposedActionDraft {
  proposal: CopilotToolProposal;
  origin: "server" | "browser-mock";
  label: string;
}

export interface CopilotActionState {
  draft: CopilotProposedActionDraft;
  preview: CopilotBrowserToolResult | null;
  applied: CopilotBrowserToolResult | null;
  busy: "preview" | "apply" | null;
  error: string | null;
}

export type CopilotTurnTransport = (
  input: CopilotTurnRequest,
  options?: PostCopilotTurnOptions
) => Promise<CopilotTurnResponse>;

const defaultCopilotTransport: CopilotTurnTransport = async (input, options) => {
  const { postCopilotTurn } = await import("../../liveLanes");

  return postCopilotTurn(input, options);
};

export type CopilotProposalFactory = (input: {
  response: CopilotTurnResponse;
  message: string;
}) => CopilotProposedActionDraft | null;

export interface UseCopilotConversationOptions {
  contextId: string;
  projectId?: string | undefined;
  buildRequest: (message: string) => CopilotTurnRequest;
  hasContext?: boolean | undefined;
  initialAvailability?: LiveLaneAvailability | undefined;
  enabled?: boolean | undefined;
  transport?: CopilotTurnTransport | undefined;
  proposalFactory?: CopilotProposalFactory | undefined;
  engineFixture?: CarringtonEngineFixture | null | undefined;
  engineFixtureLoading?: boolean | undefined;
  engineFixtureError?: string | null | undefined;
  apartmentTypeId?: string | undefined;
  now?: (() => string) | undefined;
}

export interface AddCopilotMessageInput
  extends Omit<AssistantMessage, "createdAt" | "id"> {
  id?: string;
}

export interface UseCopilotConversationResult {
  availability: LiveLaneAvailability;
  groundingState: AssistantGroundingState;
  messages: readonly AssistantMessage[];
  turn?: AssistantTurnState;
  response: CopilotTurnResponse | null;
  actionState: CopilotActionState | null;
  error: string | null;
  isBusy: boolean;
  canAsk: boolean;
  unavailableMessage: string | undefined;
  send: (raw: string) => Promise<void>;
  addMessage: (message: AddCopilotMessageInput) => string;
  runAction: (confirmed: boolean) => Promise<void>;
}

const MAX_RETAINED_MESSAGES = 40;

export function useCopilotConversation({
  contextId,
  projectId,
  buildRequest,
  hasContext = true,
  initialAvailability,
  enabled = true,
  transport = defaultCopilotTransport,
  proposalFactory,
  engineFixture = null,
  apartmentTypeId,
  now = () => new Date().toISOString()
}: UseCopilotConversationOptions): UseCopilotConversationResult {
  const [availability, setAvailability] = useState<LiveLaneAvailability>(
    initialAvailability ?? { state: "checking" }
  );
  const [messagesByContext, setMessagesByContext] = useState<
    Record<string, AssistantMessage[]>
  >({});
  const [turnsByContext, setTurnsByContext] = useState<
    Record<string, AssistantTurnState>
  >({});
  const [responsesByContext, setResponsesByContext] = useState<
    Record<string, CopilotTurnResponse | null>
  >({});
  const [actionsByContext, setActionsByContext] = useState<
    Record<string, CopilotActionState | null>
  >({});
  const [errorsByContext, setErrorsByContext] = useState<
    Record<string, string | null>
  >({});
  const [busyByContext, setBusyByContext] = useState<Record<string, boolean>>({});
  const messageSequence = useRef(0);
  const requestSequence = useRef(0);

  useEffect(() => {
    if (initialAvailability) {
      setAvailability(initialAvailability);
    }
  }, [initialAvailability]);

  useEffect(() => {
    if (!enabled || initialAvailability) {
      return;
    }

    let cancelled = false;

    const checkHealth = async () => {
      try {
        const { fetchLiveHealth } = await import("../../liveLanes");
        const health = await fetchLiveHealth();

        if (!cancelled) {
          setAvailability(copilotAvailabilityFromHealth(health));
        }
      } catch (error) {
        if (!cancelled) {
          setAvailability({
            state: "unavailable",
            message:
              error instanceof Error
                ? error.message
                : "The live assistant service is not reachable."
          });
        }
      }
    };

    void checkHealth();

    return () => {
      cancelled = true;
    };
  }, [enabled, initialAvailability]);

  const addMessage = useCallback(
    (message: AddCopilotMessageInput): string => {
      messageSequence.current += 1;
      const id = message.id ?? `copilot-${message.role}-${messageSequence.current}`;
      const nextMessage: AssistantMessage = {
        ...message,
        id,
        createdAt: now()
      };

      setMessagesByContext((current) => ({
        ...current,
        [contextId]: [...(current[contextId] ?? []), nextMessage].slice(
          -MAX_RETAINED_MESSAGES
        )
      }));

      return id;
    },
    [contextId, now]
  );

  const send = useCallback(
    async (raw: string): Promise<void> => {
      const message = raw.trim();
      const isBusy = busyByContext[contextId] ?? false;

      if (!message || isBusy) {
        return;
      }

      addMessage({ role: "user", text: message });
      setErrorsByContext((current) => ({ ...current, [contextId]: null }));
      setResponsesByContext((current) => ({ ...current, [contextId]: null }));
      setActionsByContext((current) => ({ ...current, [contextId]: null }));

      if (!enabled || availability.state !== "ready") {
        const unavailableText =
          availability.state === "checking"
            ? "The assistant is still checking availability. Try again in a moment."
            : "The assistant is unavailable right now. No answer was generated.";
        const error = new Error(unavailableText);

        setTurnsByContext((current) => ({
          ...current,
          [contextId]: {
            status: "error",
            events: [],
            transportError: unavailableText
          }
        }));
        addMessage({ role: "error", text: unavailableText });
        setErrorsByContext((current) => ({
          ...current,
          [contextId]: unavailableText
        }));
        throw error;
      }

      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      setBusyByContext((current) => ({ ...current, [contextId]: true }));
      setTurnsByContext((current) => ({
        ...current,
        [contextId]: { status: "streaming", events: [] }
      }));

      try {
        const request = buildRequest(message);
        const response = await transport(request, {
          onEvent: (event) => {
            if (requestSequence.current !== requestId) {
              return;
            }

            setTurnsByContext((current) => {
              const previous = current[contextId] ?? {
                status: "streaming" as const,
                events: []
              };

              return {
                ...current,
                [contextId]: {
                  ...previous,
                  status: "streaming",
                  events: [...previous.events, event]
                }
              };
            });
          }
        });

        if (requestSequence.current !== requestId) {
          return;
        }

        const events = assistantEventsForResponse(response.answer, response.events);
        const refused = events.some((event) => event.type === "refusal");
        const grounded = isGroundedAnswer(events);
        const answer = response.answer.trim() || finalAnswerFromEvents(events);
        const { citationsFromProvenance } = await import("../assistant/citations");
        const citations = citationsFromProvenance(
          events.filter(
            (event): event is Extract<AssistantTurnEvent, { type: "tool_result" }> =>
              event.type === "tool_result"
          ),
          projectId
        );
        const proposal = response.toolProposal
          ? {
              proposal: response.toolProposal,
              origin: "server" as const,
              label: "Server proposal"
            }
          : proposalFactory?.({ response, message }) ?? null;

        setTurnsByContext((current) => ({
          ...current,
          [contextId]: {
            status: refused ? "refused" : "complete",
            events
          }
        }));
        setResponsesByContext((current) => ({
          ...current,
          [contextId]: response
        }));
        setActionsByContext((current) => ({
          ...current,
          [contextId]: proposal
            ? {
                draft: proposal,
                preview: null,
                applied: null,
                busy: null,
                error: null
              }
            : null
        }));

        if (answer || refused) {
          addMessage({
            role: "assistant",
            text: answer || "The assistant declined this turn.",
            providerLabel: response.provider.model,
            grounding: grounded ? "grounded" : "ungrounded",
            technicalDetails: assistantAnswerTechnicalDetails(response),
            ...(citations.length > 0 ? { citations } : {})
          });
        }
      } catch (error) {
        const transportError =
          error instanceof Error
            ? error.message
            : "The assistant connection stopped before the answer completed.";

        setTurnsByContext((current) => ({
          ...current,
          [contextId]: {
            status: "error",
            events: current[contextId]?.events ?? [],
            transportError
          }
        }));
        setErrorsByContext((current) => ({
          ...current,
          [contextId]: transportError
        }));
        addMessage({
          role: "error",
          text: `The assistant connection stopped mid-turn. ${transportError}`
        });
        throw error instanceof Error ? error : new Error(transportError);
      } finally {
        if (requestSequence.current === requestId) {
          setBusyByContext((current) => ({ ...current, [contextId]: false }));
        }
      }
    },
    [
      addMessage,
      availability.state,
      busyByContext,
      buildRequest,
      contextId,
      enabled,
      projectId,
      proposalFactory,
      transport
    ]
  );

  const runAction = useCallback(
    async (confirmed: boolean): Promise<void> => {
      const actionState = actionsByContext[contextId];

      if (!actionState || !engineFixture || !apartmentTypeId) {
        return;
      }

      if (
        confirmed &&
        actionState.draft.proposal.toolName === "overrides.applyScopedOverride" &&
        !actionState.preview
      ) {
        return;
      }

      setActionsByContext((current) => ({
        ...current,
        [contextId]: {
          ...actionState,
          busy: confirmed ? "apply" : "preview",
          error: null
        }
      }));

      try {
        const result = await runCopilotToolProposalAsync({
          proposal: actionState.draft.proposal,
          fixture: engineFixture,
          apartmentTypeId,
          actor: {
            id: "project-lead",
            displayName: "Project Lead",
            role: "spec_reviewer"
          },
          now: now(),
          confirmed
        });

        setActionsByContext((current) => ({
          ...current,
          [contextId]: {
            ...actionState,
            preview: confirmed ? actionState.preview : result,
            applied: confirmed ? result : actionState.applied,
            busy: null,
            error: null
          }
        }));
      } catch (error) {
        setActionsByContext((current) => ({
          ...current,
          [contextId]: {
            ...actionState,
            busy: null,
            error:
              error instanceof Error
                ? error.message
                : "Could not run the action verification guard."
          }
        }));
      }
    },
    [actionsByContext, apartmentTypeId, contextId, engineFixture, now]
  );

  const currentTurn = turnsByContext[contextId];
  const currentAction = actionsByContext[contextId] ?? null;
  const currentError = errorsByContext[contextId] ?? null;
  const currentBusy = busyByContext[contextId] ?? false;
  const canAsk = enabled && availability.state === "ready" && !currentBusy;

  return {
    availability,
    groundingState: assistantGroundingStateFromAvailability(availability, hasContext),
    messages: messagesByContext[contextId] ?? [],
    ...(currentTurn ? { turn: currentTurn } : {}),
    response: responsesByContext[contextId] ?? null,
    actionState: currentAction,
    error: currentError,
    isBusy: currentBusy,
    canAsk,
    unavailableMessage: canAsk ? undefined : unavailableMessageForInput(availability),
    send,
    addMessage,
    runAction
  };
}

export function assistantGroundingStateFromAvailability(
  availability: LiveLaneAvailability,
  hasContext: boolean
): AssistantGroundingState {
  if (availability.state === "checking") {
    return { status: "checking", label: "Checking" };
  }

  if (availability.state !== "ready") {
    return {
      status: "offline",
      label: "Unavailable",
      detail: "The live assistant is unavailable right now. No answer will be generated."
    };
  }

  return hasContext
    ? {
        status: "ready",
        label: "Live assistant ready",
        detail: "Live answers use the current project context and retrieval trace."
      }
    : {
        status: "empty",
        label: "Needs project facts",
        detail: "Add project details first so the assistant has useful context."
      };
}

export function assistantEventsForResponse(
  answer: string,
  events: readonly AssistantTurnEvent[]
): AssistantTurnEvent[] {
  if (events.length > 0) {
    return [...events];
  }

  return answer.trim() ? [{ type: "final_answer", answer }] : [];
}

export function isGroundedAnswer(events: readonly AssistantTurnEvent[]): boolean {
  return events.some(
    (event) => event.type === "tool_result" && event.provenance.length > 0
  );
}

function finalAnswerFromEvents(events: readonly AssistantTurnEvent[]): string {
  const finalAnswer = [...events]
    .reverse()
    .find(
      (event): event is Extract<AssistantTurnEvent, { type: "final_answer" }> =>
        event.type === "final_answer"
    );

  return finalAnswer?.answer.trim() ?? "";
}

function assistantAnswerTechnicalDetails(
  response: CopilotTurnResponse
): AssistantTechnicalDetails {
  return {
    sources: [
      {
        label: "Assistant answer",
        detail: "Response details are available here."
      }
    ],
    checks: [
      {
        label: "Provider response",
        detail: response.provider.model
          ? "The selected live assistant returned this answer."
          : "The live assistant returned this answer."
      }
    ],
    rawData: {
      provider: response.provider,
      turn: response.turn
    }
  };
}

function unavailableMessageForInput(availability: LiveLaneAvailability): string | undefined {
  if (availability.state === "checking") {
    return "Checking live assistant availability…";
  }

  if (availability.state === "ready") {
    return undefined;
  }

  return "Live assistant unavailable — no answer will be generated.";
}

function liveUnavailableMessage(health: LiveHealthResponse): string {
  const plainReason = copilotHealthUnavailableDetail(health);
  const noFallbackReason =
    "It will stay paused until the selected service is available.";

  return health.livePath.noMockFallback
    ? `${plainReason} ${noFallbackReason}`
    : plainReason;
}

type CopilotHealthCapabilityState = "ready" | "offline" | "disabled" | "unavailable";

function copilotHealthCapabilityState(
  health: LiveHealthResponse
): CopilotHealthCapabilityState {
  if (!health.ok) {
    return "unavailable";
  }

  if (
    health.providerAvailable &&
    health.livePath.available &&
    health.livePath.readiness === "ready"
  ) {
    return "ready";
  }

  if (health.livePath.readiness === "disabled") {
    return health.providerAvailable ? "offline" : "disabled";
  }

  return "unavailable";
}

function copilotProviderDisplayName(provider: string): string {
  const normalized = provider.trim().toLowerCase();

  if (!normalized || normalized === "disabled" || normalized === "mock") {
    return "AI";
  }

  return normalized
    .split(/[-_\s]+/u)
    .filter(Boolean)
    .map((word) =>
      word === "openai"
        ? "OpenAI"
        : word === "ai"
          ? "AI"
          : `${word.charAt(0).toUpperCase()}${word.slice(1)}`
    )
    .join(" ");
}

function copilotHealthUnavailableDetail(health: LiveHealthResponse): string {
  const currentProvider = copilotProviderDisplayName(health.provider);
  const providerName =
    currentProvider === "AI"
      ? copilotProviderDisplayName(
          health.llmSettings?.provider ??
            health.requestedProvider ??
            health.livePath.requestedProvider
        )
      : currentProvider;

  return health.livePath.configured
    ? `${providerName} is unavailable right now.`
    : `${providerName} is selected but not configured.`;
}
